import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { 
  AlertTriangle, 
  XCircle, 
  Clock, 
  User, 
  MapPin,
  ChevronRight,
  RefreshCw,
  Filter,
  ArrowUpRight,
  Calendar,
  Timer,
  Edit3,
  X
} from "lucide-react";
import { useTranslation } from 'react-i18next';
import { api } from "../App";
import { toast } from "sonner";
import { format, formatDistanceToNow } from "date-fns";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import StatusBadge from "./StatusBadge";
import SLATimelineModal from "./dashboard/SLATimelineModal";
import { LoadingOverlay } from "./LoadingOverlay";

// Super authority designations that can edit timelines
const SUPER_AUTHORITIES = ["tahsildar", "ddlr", "adlr"];

export default function SLAViolationsPage({ user }) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const currentLang = i18n.language;
  
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [violations, setViolations] = useState([]);
  const [warnings, setWarnings] = useState([]);
  const [stats, setStats] = useState(null);
  const [filterType, setFilterType] = useState("all"); // all, violations, warnings
  const [filterDesignation, setFilterDesignation] = useState(null); // Filter by designation
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [showTimelineModal, setShowTimelineModal] = useState(false);

  // Check if user is a super authority
  const isSuperAuthority = user?.designation && SUPER_AUTHORITIES.includes(user.designation);

  // Determine user's department from designation
  const getUserDepartment = () => {
    const revenueDesignations = ["tahsildar", "deputy_tahsildar", "shirestedar", "revenue_inspector", "village_accountant", "first_division_clerk", "second_division_clerk"];
    const surveyDesignations = ["ddlr", "adlr", "surveyor", "survey_inspector", "asst_surveyor"];
    
    if (user?.designation) {
      if (revenueDesignations.includes(user.designation)) return "revenue";
      if (surveyDesignations.includes(user.designation)) return "survey";
    }
    return user?.department || null;
  };

  const userDepartment = getUserDepartment();

  const fetchData = async () => {
    try {
      // Build query params with department filter
      const deptParam = userDepartment ? `department=${userDepartment}` : '';
      
      // Fetch SLA dashboard stats (filtered by department)
      const statsUrl = deptParam ? `/sla/dashboard?${deptParam}` : '/sla/dashboard';
      const statsRes = await api.get(statsUrl);
      setStats(statsRes.data);

      // Fetch violations (filtered by department)
      const violationsUrl = deptParam ? `/sla/violations?${deptParam}&limit=100` : '/sla/violations?limit=100';
      const violationsRes = await api.get(violationsUrl);
      // Deduplicate by ID (safety measure)
      const uniqueViolations = [...new Map((violationsRes.data.violations || []).map(v => [v.id, v])).values()];
      setViolations(uniqueViolations);

      // Fetch warnings using the dedicated endpoint (filtered by department)
      const warningsUrl = deptParam ? `/sla/warnings?${deptParam}&limit=100` : '/sla/warnings?limit=100';
      const warningsRes = await api.get(warningsUrl);
      // Deduplicate by ID (safety measure)
      const uniqueWarnings = [...new Map((warningsRes.data.warnings || []).map(w => [w.id, w])).values()];
      setWarnings(uniqueWarnings);
    } catch (error) {
      console.error('Failed to fetch SLA data:', error);
      toast.error(t('common.error') || 'Failed to load SLA data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleRefresh = async () => {
    setRefreshing(true);
    // Reset filters to default
    setFilterType("all");
    setFilterDesignation(null);
    await fetchData();
  };

  const handleViewApplication = (appId) => {
    navigate(`/application/${appId}`);
  };

  const handleEditTimeline = (app, e) => {
    e.stopPropagation();
    setSelectedApplication(app);
    setShowTimelineModal(true);
  };

  const handleTimelineUpdate = () => {
    fetchData(); // Refresh data after timeline update
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return format(new Date(dateStr), 'MMM dd, yyyy');
    } catch {
      return dateStr;
    }
  };

  const formatTimeAgo = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return formatDistanceToNow(new Date(dateStr), { addSuffix: true });
    } catch {
      return dateStr;
    }
  };

  // Combine and filter data - using useMemo for better performance and guaranteed updates
  const filteredData = React.useMemo(() => {
    let data = [];
    
    // Add violations if filter is "all" or "violations"
    if (filterType === "all" || filterType === "violations") {
      data = [...data, ...violations.map(v => ({ ...v, type: 'violation' }))];
    }
    
    // Add warnings if filter is "all" or "warnings"
    if (filterType === "all" || filterType === "warnings") {
      data = [...data, ...warnings.map(w => ({ ...w, type: 'warning' }))];
    }
    
    // Filter by designation if selected
    if (filterDesignation) {
      data = data.filter(item => {
        // Normalize the designation - treat null, undefined, empty string, "null" as 'unassigned'
        const itemDesignation = item.assigned_official_designation;
        const normalizedDesignation = (!itemDesignation || itemDesignation === 'null' || itemDesignation === 'undefined') 
          ? 'unassigned' 
          : itemDesignation;
        
        return normalizedDesignation === filterDesignation;
      });
    }
    
    // Sort by urgency - violations first, then by days overdue
    return data.sort((a, b) => {
      if (a.type === 'violation' && b.type !== 'violation') return -1;
      if (a.type !== 'violation' && b.type === 'violation') return 1;
      return (b.days_overdue || 0) - (a.days_overdue || 0);
    });
  }, [filterType, filterDesignation, violations, warnings]);
  
  // Handle filter type change
  const handleFilterTypeChange = (value) => {
    setFilterType(value);
  };
  
  // Handle designation filter click
  const handleDesignationFilter = (designation) => {
    if (filterDesignation === designation) {
      setFilterDesignation(null); // Toggle off if same
    } else {
      setFilterDesignation(designation);
    }
  };
  
  // Clear designation filter
  const clearDesignationFilter = () => {
    setFilterDesignation(null);
  };

  if (loading) {
    return <LoadingOverlay fullPage={false} />;
  }

  return (
    <div className="space-y-6" data-testid="sla-violations-page">
      {/* Header Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="p-4 bg-white border border-stone-200" data-testid="sla-total-card">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-slate-100 rounded-lg">
              <Timer className="w-5 h-5 text-slate-700" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900">{stats?.total_with_sla || 0}</p>
              <p className="text-xs text-slate-500">{t('sla.totalWithSla')}</p>
            </div>
          </div>
        </Card>
        
        <Card className="p-4 bg-emerald-50 border border-emerald-200" data-testid="sla-ontrack-card">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-emerald-100 rounded-lg">
              <Clock className="w-5 h-5 text-emerald-700" />
            </div>
            <div>
              <p className="text-2xl font-bold text-emerald-700">{stats?.on_track || 0}</p>
              <p className="text-xs text-emerald-600">{t('sla.onTrack')}</p>
            </div>
          </div>
        </Card>
        
        <Card className="p-4 bg-amber-50 border border-amber-200" data-testid="sla-warning-card">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-amber-100 rounded-lg">
              <AlertTriangle className="w-5 h-5 text-amber-700" />
            </div>
            <div>
              <p className="text-2xl font-bold text-amber-700">{stats?.warning || 0}</p>
              <p className="text-xs text-amber-600">{t('sla.warningShort') || 'Warning'}</p>
            </div>
          </div>
        </Card>
        
        <Card className="p-4 bg-red-50 border border-red-200" data-testid="sla-violated-card">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-red-100 rounded-lg">
              <XCircle className="w-5 h-5 text-red-700" />
            </div>
            <div>
              <p className="text-2xl font-bold text-red-700">{stats?.violated || 0}</p>
              <p className="text-xs text-red-600">{t('sla.violations')}</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
        <div className="flex flex-wrap gap-3 items-center">
          <Select value={filterType} onValueChange={handleFilterTypeChange}>
            <SelectTrigger className="w-[180px]" data-testid="sla-type-filter">
              <Filter className="w-4 h-4 mr-2" />
              <SelectValue placeholder={t('sla.filterByType') || 'Filter by type'} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('common.all') || 'All'}</SelectItem>
              <SelectItem value="violations">{t('sla.violations')}</SelectItem>
              <SelectItem value="warnings">{t('sla.warningShort') || 'Warnings'}</SelectItem>
            </SelectContent>
          </Select>
          
          {/* Show current department filter info */}
          {userDepartment && (
            <Badge variant="outline" className={`text-xs ${userDepartment === 'revenue' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-blue-50 text-blue-700 border-blue-200'}`}>
              {userDepartment === 'revenue' ? t('department.revenue') : t('department.survey')}
            </Badge>
          )}
          
          {/* Show active designation filter */}
          {filterDesignation && (
            <Badge 
              variant="outline" 
              className="text-xs bg-red-50 text-red-700 border-red-200 cursor-pointer hover:bg-red-100"
              onClick={clearDesignationFilter}
            >
              {filterDesignation === 'unassigned' 
                ? (t('sla.unassigned') || 'Unassigned')
                : (t(`designation.${filterDesignation}`) || filterDesignation)
              }
              <X className="w-3 h-3 ml-1" />
            </Badge>
          )}
        </div>
        
        <Button
          variant="outline"
          size="sm"
          onClick={handleRefresh}
          disabled={refreshing}
          className="flex items-center gap-2"
          data-testid="sla-refresh-btn"
        >
          <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
          {refreshing ? (t('common.refreshing') || 'Refreshing...') : (t('common.refresh') || 'Refresh')}
        </Button>
      </div>

      {/* Results Count and Violations by Role - Same Row */}
      <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center justify-between">
        {/* Results Count - Blue color */}
        <div className="text-sm font-medium text-blue-600" data-testid="sla-results-count">
          {t('common.showing') || 'Showing'} <span className="font-bold">{filteredData.length}</span> {t('sla.issuesFound') || 'issues found'}
        </div>
        
        {/* Violations by Designation - Inline */}
        {stats?.violations_by_designation && Object.keys(stats.violations_by_designation).length > 0 && (
          <div className="flex flex-wrap items-center gap-2" data-testid="sla-violations-by-role">
            <span className="text-sm font-medium text-red-700 flex items-center gap-1">
              <XCircle className="w-4 h-4" />
              {t('sla.violationsByRole') || 'Violations by Role'}:
            </span>
            {Object.entries(stats.violations_by_designation).map(([designation, count]) => {
              // Handle null/undefined designation
              const displayDesignation = designation === 'null' || designation === 'undefined' || !designation 
                ? 'unassigned' 
                : designation;
              const isActive = filterDesignation === displayDesignation;
              
              return (
                <button
                  key={designation}
                  onClick={() => handleDesignationFilter(displayDesignation)}
                  className={`flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 border text-sm transition-colors cursor-pointer ${
                    isActive 
                      ? 'bg-red-600 text-white border-red-600' 
                      : 'bg-white text-slate-700 border-red-200 hover:bg-red-50 hover:border-red-300'
                  }`}
                  data-testid={`filter-designation-${displayDesignation}`}
                >
                  <User className="w-3.5 h-3.5" />
                  <span className="font-medium">
                    {displayDesignation === 'unassigned' 
                      ? (t('sla.unassigned') || 'Unassigned')
                      : (t(`designation.${displayDesignation}`) || displayDesignation)
                    }
                  </span>
                  <Badge className={`text-xs ${isActive ? 'bg-white text-red-600' : 'bg-red-600 text-white'}`}>
                    {count}
                  </Badge>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Issues List */}
      {filteredData.length === 0 ? (
        <Card className="p-8 text-center bg-emerald-50 border border-emerald-200" data-testid="sla-no-issues">
          <Clock className="w-12 h-12 text-emerald-600 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-emerald-800 mb-2">
            {t('sla.noIssues') || 'No SLA Issues'}
          </h3>
          <p className="text-sm text-emerald-600">
            {t('sla.allOnTrack') || 'All applications are being processed on time'}
          </p>
        </Card>
      ) : (
        <div className="space-y-3">
          {filteredData.map((item, index) => (
            <Card 
              key={`${item.type}-${item.id || index}`}
              className={`p-4 border-l-4 ${
                item.type === 'violation' 
                  ? 'border-l-red-500 bg-red-50/30' 
                  : 'border-l-amber-500 bg-amber-50/30'
              }`}
              data-testid={`sla-issue-card-${item.id}`}
            >
              <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                {/* Left Side - Main Info */}
                <div className="flex-1 space-y-2">
                  <div className="flex items-center gap-2 flex-wrap">
                    {/* Type Badge */}
                    <Badge 
                      variant="outline" 
                      className={`${
                        item.type === 'violation'
                          ? 'bg-red-100 text-red-800 border-red-300'
                          : 'bg-amber-100 text-amber-800 border-amber-300'
                      }`}
                    >
                      {item.type === 'violation' ? (
                        <><XCircle className="w-3 h-3 mr-1" />{t('sla.violations')}</>
                      ) : (
                        <><AlertTriangle className="w-3 h-3 mr-1" />{t('sla.warningShort') || 'Warning'}</>
                      )}
                    </Badge>
                    
                    {/* Status Badge */}
                    <StatusBadge status={item.status} />
                    
                    {/* Department Badge */}
                    <Badge variant="outline" className="bg-white">
                      {t(`department.${item.department}`)}
                    </Badge>
                  </div>
                  
                  {/* Application Details */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 text-sm">
                    <div className="flex items-center gap-2">
                      <MapPin className="w-4 h-4 text-slate-400" />
                      <span className="font-medium">{item.land_survey_number || '-'}</span>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <User className="w-4 h-4 text-slate-400" />
                      <span>{item.farmer_name || '-'}</span>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <User className="w-4 h-4 text-slate-400" />
                      <span className="text-slate-600">
                        {item.assigned_official_name || '-'} 
                        {item.assigned_official_designation && (
                          <span className="text-xs text-slate-400 ml-1">
                            ({t(`designation.${item.assigned_official_designation}`)})
                          </span>
                        )}
                      </span>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <Calendar className="w-4 h-4 text-slate-400" />
                      <span className="text-slate-600">
                        {t('sla.assignedOn') || 'Assigned'}: {formatDate(item.assigned_at)}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Right Side - SLA Info & Actions */}
                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                  {/* Days Info */}
                  <div className={`text-center px-4 py-2 rounded-lg ${
                    item.type === 'violation' ? 'bg-red-100' : 'bg-amber-100'
                  }`}>
                    <p className={`text-2xl font-bold ${
                      item.type === 'violation' ? 'text-red-700' : 'text-amber-700'
                    }`}>
                      {item.type === 'violation' 
                        ? (item.days_overdue || item.sla_status?.days_overdue || 0)
                        : (item.days_remaining || item.sla_status?.days_remaining || 0)
                      }
                    </p>
                    <p className={`text-xs ${
                      item.type === 'violation' ? 'text-red-600' : 'text-amber-600'
                    }`}>
                      {item.type === 'violation' 
                        ? (t('sla.daysOverdue') || 'Days Overdue')
                        : (t('sla.daysRemaining') || 'Days Left')
                      }
                    </p>
                  </div>
                  
                  {/* Deadline Info */}
                  <div className="text-sm text-slate-500">
                    <p>{t('sla.deadline')}: {formatDate(item.sla_deadline || item.sla?.deadline)}</p>
                    <p className="text-xs">{formatTimeAgo(item.sla_deadline || item.sla?.deadline)}</p>
                  </div>
                  
                  {/* Action Buttons */}
                  <div className="flex flex-col sm:flex-row gap-2">
                    {/* Edit Timeline Button - Only for Super Authorities */}
                    {isSuperAuthority && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={(e) => handleEditTimeline(item, e)}
                        className="flex items-center gap-1 text-amber-700 border-amber-300 hover:bg-amber-50"
                        data-testid={`edit-timeline-${item.id}`}
                      >
                        <Edit3 className="w-4 h-4" />
                        {t('sla.editTimeline') || 'Edit Timeline'}
                      </Button>
                    )}
                    
                    {/* View Button */}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleViewApplication(item.id)}
                      className="flex items-center gap-1"
                      data-testid={`view-app-${item.id}`}
                    >
                      {t('common.view') || 'View'}
                      <ArrowUpRight className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* SLA Timeline Modal */}
      {isSuperAuthority && (
        <SLATimelineModal
          isOpen={showTimelineModal}
          onClose={() => {
            setShowTimelineModal(false);
            setSelectedApplication(null);
          }}
          application={selectedApplication}
          onUpdate={handleTimelineUpdate}
        />
      )}
    </div>
  );
}
